import React, { useCallback, useMemo } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { Alert, Spin } from "antd";
import Settings from "../settings.component";
import PlanSelection from "./components/PlanSelection";
import BillingWizard from "./components/BillingWizard";
import BillingStatusView from "./components/BillingStatusView";
import { stepItems } from "./billingSettings.constants";
import useBillingSettings from "./useBillingSettings";
import "./billingSettings.styles.scss";
import { cancelSubscription as cancelSubscriptionRequest } from "../../../../service/billing/subscriptionService";
import { STRIPE_PUBLIC_KEY } from "../../../../config";

const stripePromise = STRIPE_PUBLIC_KEY ? loadStripe(STRIPE_PUBLIC_KEY) : null;

const BillingSettings = () => {
  const {
    loading,
    error,
    isFirstTime,
    billingData,
    wizardActive,
    selectedPlan,
    selectedPlanId,
    userCount,
    setUserCount,
    existingBilling,
    plans,
    invoices,
    invoicesLoading,
    invoicesError,
    formatCurrency,
    handlePlanHighlight,
    handlePlanConfirm,
    handleWizardCancel,
    handleWizardComplete,
    handleReturnToStatus,
    refreshInvoices,
    waitForSubscriptionUpdate,
  } = useBillingSettings();

  const handleCancelSubscription = useCallback(
    async (subscriptionId) => {
      const response = await cancelSubscriptionRequest(subscriptionId);

      waitForSubscriptionUpdate(
        (summary) => {
          if (!summary) {
            return false;
          }

          if (subscriptionId && summary.subscriptionId !== subscriptionId) {
            return false;
          }

          const normalizedStatus = String(summary.status || "").toLowerCase();
          return (
            summary.cancelAtPeriodEnd === true ||
            normalizedStatus === "canceled"
          );
        },
        { timeoutMs: 60000, pollIntervalMs: 4000 }
      )
        .then(() => {
          refreshInvoices().catch(() => {});
        })
        .catch(() => {});

      return response;
    },
    [refreshInvoices, waitForSubscriptionUpdate]
  );

  const elementsOptions = useMemo(
    () => ({
      appearance: {
        theme: "flat",
        variables: {
          colorPrimary: "#111827",
          colorBackground: "#ffffff",
          colorText: "#111827",
        },
      },
    }),
    []
  );

  if (!stripePromise) {
    return (
      <div className="full-height">
        <Settings />
        <div className="billing-settings-container">
          <Alert type="error" showIcon message="Billing not configured" />
        </div>
      </div>
    );
  }

  const renderFirstTimeContent = () => (
    <Elements stripe={stripePromise} options={elementsOptions}>
      {wizardActive ? (
        <BillingWizard
          stepItems={stepItems}
          selectedPlan={selectedPlan}
          userCount={userCount}
          formatCurrency={formatCurrency}
          onCancel={handleWizardCancel}
          onComplete={handleWizardComplete}
          onReturnToStatus={handleReturnToStatus}
        />
      ) : (
        <PlanSelection
          planOptions={plans}
          userCount={userCount}
          onUserCountChange={setUserCount}
          selectedPlanId={selectedPlanId}
          onPlanHighlight={handlePlanHighlight}
          onPlanConfirm={handlePlanConfirm}
          formatCurrency={formatCurrency}
          billingData={billingData}
        />
      )}
    </Elements>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <div className="billing-loading">
          <Spin size="large" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="billing-loading">
          <Alert
            type="error"
            showIcon
            message="Unable to load billing information"
            description={error?.message || "Please try again later."}
          />
        </div>
      );
    }

    if (isFirstTime) {
      return renderFirstTimeContent();
    }

    return (
      <BillingStatusView
        summary={existingBilling}
        invoices={invoices}
        invoicesLoading={invoicesLoading}
        invoicesError={invoicesError}
        onReloadInvoices={refreshInvoices}
        formatCurrency={formatCurrency}
        onCancelSubscription={handleCancelSubscription}
      />
    );
  };

  return (
    <div className="full-height">
      <Settings />
      <div className="billing-settings-container">{renderContent()}</div>
    </div>
  );
};

export default BillingSettings;
